import React, { useState, useEffect } from 'react';
import { Upload, Send, X, Loader2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import { uploadMultipleFiles, type UploadedFile } from '@/lib/storage';
import { toast } from 'sonner';
import { sendEstimationEmail } from '@/service/emailService';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
const EstimationForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    budget: '',
    message: '',
    files: [] as File[],
    // Honeypot field - should remain empty
    website: '',
  });

  // Rate limiting: prevent submissions more than once per minute
  const canSubmit = () => {
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;
    return timeSinceLastSubmission > 60000; // 1 minute
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check honeypot field - if filled, it's likely a bot
    if (formData.website) {
      console.log('Bot detected via honeypot');
      return;
    }
    
    // Rate limiting check
    if (!canSubmit()) {
      toast.error('Veuillez attendre avant de soumettre à nouveau');
      return;
    }
    
    // Enhanced validation
    if (!formData.name || !formData.email || !formData.phone || !formData.budget || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validate name (should contain at least 2 words and be realistic)
    const nameWords = formData.name.trim().split(/\s+/);
    if (nameWords.length < 2 || formData.name.length < 3) {
      toast.error('Veuillez entrer votre nom complet');
      return;
    }

    // Validate email format more strictly
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    // Validate phone (should contain numbers and be reasonable length)
    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 8 || phoneNumbers.length > 15) {
      toast.error('Veuillez entrer un numéro de téléphone valide');
      return;
    }

    // Validate message length (should be substantial)
    if (formData.message.trim().length < 20) {
      toast.error('Veuillez fournir une description plus détaillée de votre projet');
      return;
    }

    setIsLoading(true);
    setLastSubmissionTime(Date.now());
    
    try {
      // Execute reCAPTCHA
      let recaptchaToken = '';
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('estimation_form');
      }

      //console.log(formData)
      // Send email with file URLs and reCAPTCHA token
      await sendEstimationEmail({ ...formData, recaptchaToken });
      toast.success('Votre demande d\'estimation a été envoyée avec succès!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        budget: '',
        message: '',
        files: [],
        website: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      // Check file size (10MB limit)
      const validFiles = newFiles.filter(file => {
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          toast.error(`Le fichier ${file.name} dépasse la limite de 10MB`);
          return false;
        }
        return true;
      });
      
      // Limit to 3 files maximum
      const totalFiles = [...formData.files, ...validFiles];
      if (totalFiles.length > 3) {
        toast.error('Maximum 3 fichiers autorisés');
        return;
      }

      setFormData(prev => ({ ...prev, files: totalFiles }));
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  return (
    <section id="estimation" className="relative py-20 overflow-hidden bg-gradient-to-br from-orcsys-50 via-white to-orcsys-50">
      {/* Orbiting tech elements */}


      <div className="container mx-auto px-4 relative">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Demander une estimation
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Remplissez le formulaire ci-dessous pour recevoir une estimation personnalisée de votre projet.
              Nous vous répondrons sous 24h ouvrées.
            </p>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-12 border border-white/20">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Votre nom complet"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    required
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="01 23 45 67 89"
                  />
                </div>

                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-slate-700 mb-2">
                    Budget estimé *
                  </label>
                  <select
                    id="budget"
                    required
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-white appearance-none"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                    value={formData.budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                  >
                    <option value="">Sélectionnez une fourchette</option>
                    <option value="1 000€ - 5 000€">1 000€ - 5 000€</option>
                    <option value="5 000€ - 10 000€">5 000€ - 10 000€</option>
                    <option value="10 000€ - 20 000€">10 000€ - 20 000€</option>
                    <option value="20 000€ - 50 000€">20 000€ - 50 000€</option>
                    <option value="50 000€ et plus">50 000€ et plus</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Description du projet *
                </label>
                <textarea
                  id="message"
                  required
                  disabled={isLoading}
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-orcsys-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Décrivez votre projet, ses objectifs et vos attentes..."
                />
              </div>

              {/* Honeypot field - hidden from users but visible to bots */}
              <div style={{ display: 'none' }}>
                <label htmlFor="website">Website (leave empty)</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Documents (optionnel)
                </label>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="flex-1">
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.txt"
                        multiple
                        disabled={isLoading}
                      />
                      <div className={`w-full px-4 py-3 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center gap-2 cursor-pointer transition-all duration-200 ${
                        isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:border-orcsys-500 hover:bg-orcsys-50'
                      }`}>
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-slate-600">
                          Ajouter des fichiers
                        </span>
                      </div>
                    </label>
                  </div>

                  {formData.files.length > 0 && (
                    <div className="space-y-2">
                      {formData.files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Upload className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-600">{file.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            disabled={isLoading}
                            className={`text-slate-400 transition-colors ${
                              isLoading ? 'cursor-not-allowed' : 'hover:text-red-500'
                            }`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  Formats acceptés : PDF, DOC, DOCX, TXT (max 10MB par fichier, 3 fichiers maximum)
                </p>
              </div>

              <div className="flex justify-center pt-4">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-orcsys-500 hover:bg-orcsys-600 text-white px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer la demande
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EstimationForm; 